import React, { useState, useEffect } from 'react';
import { Calendar, User, Shield, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { CyberGrid } from '../components/CyberGrid';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
const CACHE_KEY = 'blog_posts_cache';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

interface BlogPost {
  _id: string;
  title: string;
  slug?: string;
  excerpt: string;
  content?: string;
  author: string;
  createdAt: string;
  tags: string[];
  views: number;
  featuredImage?: string;
}

function getCachedPosts(): BlogPost[] | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { data, timestamp } = JSON.parse(raw);
    if (Date.now() - timestamp > CACHE_TTL) return null;
    return data;
  } catch {
    return null;
  }
}

function cachePosts(posts: BlogPost[]) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ data: posts, timestamp: Date.now() }));
  } catch {}
}

const SkeletonCard = () => (
  <div className="bg-white/90 rounded-2xl shadow-lg overflow-hidden border border-slate-200 animate-pulse">
    <div className="h-40 w-full bg-slate-200" />
    <div className="p-8">
      <div className="flex gap-4 mb-4">
        <div className="h-4 w-24 bg-slate-200 rounded-full" />
        <div className="h-4 w-20 bg-slate-200 rounded-full" />
      </div>
      <div className="h-6 w-full bg-slate-200 rounded mb-2" />
      <div className="h-6 w-3/4 bg-slate-200 rounded mb-4" />
      <div className="h-4 w-full bg-slate-200 rounded mb-2" />
      <div className="h-4 w-full bg-slate-200 rounded mb-2" />
      <div className="h-4 w-2/3 bg-slate-200 rounded mb-6" />
      <div className="flex gap-2">
        <div className="h-6 w-16 bg-slate-200 rounded-full" />
        <div className="h-6 w-20 bg-slate-200 rounded-full" />
      </div>
    </div>
    <div className="px-8 py-5 bg-slate-50 border-t border-slate-200">
      <div className="h-5 w-24 bg-slate-200 rounded" />
    </div>
  </div>
);

const Blog: React.FC = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const cached = getCachedPosts();
    if (cached && cached.length > 0) {
      setPosts(cached);
      setLoading(false);
      // Refresh in background without blocking UI
      axios.get(`${API_BASE_URL}/api/blog/posts`, { timeout: 30000 })
        .then((res) => {
          const data = Array.isArray(res.data) ? res.data : res.data?.items || [];
          if (data.length > 0) { setPosts(data); cachePosts(data); }
        })
        .catch(() => {});
      return;
    }

    axios.get(`${API_BASE_URL}/api/blog/posts`, { timeout: 30000 })
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : res.data?.items || [];
        setPosts(data);
        if (data.length > 0) cachePosts(data);
      })
      .catch((err) => {
        console.error('Failed to fetch blog posts:', err);
        setPosts([]);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="w-full">
      {/* HERO */}
      <section className="relative bg-gradient-to-br from-slate-950 via-slate-900 to-black text-white py-12 md:py-16 overflow-hidden">
        <CyberGrid tone="dark" animate opacity={0.08} wash={false} className="z-0" />

        {/* Soft spotlights */}
        <div aria-hidden className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute -top-24 left-10 w-[520px] h-[520px] rounded-full bg-cyan-400/10 blur-3xl" />
          <div className="absolute -bottom-24 right-10 w-[520px] h-[520px] rounded-full bg-indigo-400/10 blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-white/10 text-slate-200 text-sm font-semibold backdrop-blur-sm mb-4">
            <Shield size={16} className="text-cyan-300" />
            <span>INSIGHTS & NEWS</span>
          </div>

          <h1 className="text-5xl md:text-6xl font-extrabold leading-[1.05] tracking-tight text-white mb-6">
            Blog & Insights
          </h1>

          <p className="text-xl text-slate-300 max-w-3xl">
            Stay updated with the latest cybersecurity news, trends, and best practices.
          </p>
        </div>
      </section>

      {/* LIST */}
      <section className="relative py-12 md:py-16 bg-gradient-to-b from-slate-50 via-slate-50 to-slate-100/60 overflow-hidden">
        <CyberGrid opacity={0.05} wash className="z-0" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-20">
              <div className="mx-auto max-w-2xl bg-white/80 backdrop-blur-sm rounded-2xl border border-slate-200 shadow-lg p-10">
                <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 mb-3">
                  Insights coming soon
                </h2>
                <p className="text-slate-600 text-lg leading-relaxed">
                  We're preparing expert content on vulnerability management, continuous monitoring, and cyber risk
                  reduction. Check back soon.
                </p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {posts.map((post) => (
                <article
                  key={post._id}
                  className="group bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-slate-200 hover:border-slate-300"
                >
                  {post.featuredImage && (
                    <div className="h-40 w-full overflow-hidden">
                      <img
                        src={post.featuredImage}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}

                  <div className="p-8">
                    <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
                      <div className="flex items-center gap-1">
                        <Calendar size={16} />
                        <span>{new Date(post.createdAt).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <User size={16} />
                        <span>{post.author}</span>
                      </div>
                    </div>

                    <h3 className="text-2xl font-extrabold text-slate-900 mb-4 line-clamp-2 group-hover:opacity-90 transition-opacity">
                      {post.title}
                    </h3>

                    <p className="text-slate-600 mb-6 line-clamp-3 leading-relaxed">{post.excerpt}</p>

                    <div className="flex items-center justify-between gap-4">
                      <div className="flex gap-2 flex-wrap">
                        {post.tags.slice(0, 2).map((tag, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1.5 bg-slate-100 text-slate-700 text-xs font-bold rounded-full border border-slate-200"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                      <span className="text-sm text-slate-500 font-medium whitespace-nowrap">
                        {post.views} views
                      </span>
                    </div>
                  </div>

                  <div className="px-8 py-5 bg-gradient-to-r from-slate-50 to-white border-t border-slate-200">
                    {post.slug ? (
                      <Link
                        to={`/blog/${post.slug}`}
                        className="text-slate-900 font-bold hover:opacity-80 transition-opacity inline-flex items-center gap-2"
                      >
                        Read More <ArrowRight size={16} />
                      </Link>
                    ) : (
                      <span className="text-slate-400 font-semibold">Read More</span>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blog;
